import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Avatar } from "../components/Avatar.js";
import { MemberPicker } from "../components/MemberPicker.js";
import { SeedingAverage } from "../components/SeedingAverage.js";
import { Button, Notice } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { Refusal } from "../lib/franchises.js";
import { fetchMembers } from "../lib/members.js";
import {
  appointPanelist,
  closeSeeding,
  fetchPoolSeeding,
  fetchPublishedSeeding,
  removePanelist,
  reopenSeeding,
  SEEDING_INCOMPLETE,
} from "../lib/seeding.js";
import { isOutOfDate } from "../lib/seeding-order.js";
import { fetchTournaments } from "../lib/tournaments.js";
import type { SeedingList, Tournament, TournamentEvent } from "../lib/types.js";
import { PoolRows } from "./OrganiseRows.js";

/**
 * An auction tournament's seeding, on a page of its own like Teams: for each pool, who is on the
 * panel, who has submitted, every list with its author, the average so far, and closing and
 * reopening it. Reached from the tournament's Organise page.
 *
 * Organisers only, and only for an auction tournament. Both are signposting; the database
 * refuses the writes and sends nobody else the lists or the average.
 */
export function TournamentSeeding() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { member } = useAuth();
  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({ queryKey: ["tournaments"], queryFn: fetchTournaments });
  const tournament = tournaments?.find((candidate) => candidate.id === tournamentId);

  if (!hasRole(member, "organiser")) {
    return <Navigate to={`/tournaments/${tournamentId}`} replace />;
  }

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (error || !tournament) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link to="/events" className="micro mb-4 inline-block underline">
          ← All events
        </Link>
        <Notice tone="attention">
          {error
            ? "Couldn't load this tournament. Try again."
            : "That tournament isn't here any more."}
        </Notice>
      </main>
    );
  }

  if (!tournament.auction) {
    return <Navigate to={`/tournaments/${tournament.id}/manage`} replace />;
  }

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link
        to={`/tournaments/${tournament.id}/manage`}
        className="micro mb-4 inline-block underline"
      >
        ← {tournament.name}
      </Link>
      <PageHeader eyebrow={`Organise · ${tournament.name}`} title="Seeding" />
      <p className="mb-6 text-sm text-ink-muted">
        A panel ranks each pool, blind. Only organisers see the average; closing fixes it.
      </p>
      {/* One row per pool; each pool's seeding is a page of its own under the event. */}
      <PoolRows tournament={tournament} step="seeding" />
    </main>
  );
}

function listStatus(list: SeedingList, registered: string[]): string {
  if (list.players.length === 0 && list.skipped.length === 0) return "Not started";
  if (!list.submitted) return "Draft";
  // A skipped player is covered as much as a ranked one.
  return isOutOfDate(
    [...list.players, ...list.skipped].map((p) => p.memberId),
    registered,
  )
    ? "Out of date"
    : "Submitted";
}

export function PoolPanel({
  tournament,
  event,
}: {
  tournament: Tournament;
  event: TournamentEvent;
}) {
  const queryClient = useQueryClient();
  const closed = event.seedingClosedAt !== null;
  const started = tournament.auctionStartedAt !== null;
  const locked = closed || started;

  const { data, isPending } = useQuery({
    queryKey: ["pool-seeding", event.id],
    queryFn: () => fetchPoolSeeding(event.id),
  });
  const { data: published = [] } = useQuery({
    queryKey: ["seeding-published", event.id],
    queryFn: () => fetchPublishedSeeding(event.id),
    enabled: closed,
  });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: fetchMembers });

  const registered = event.entries.filter((e) => e.status === "registered").map((e) => e.memberId);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
    void queryClient.invalidateQueries({ queryKey: ["pool-seeding", event.id] });
    void queryClient.invalidateQueries({ queryKey: ["seeding-published", event.id] });
  };

  const appoint = useMutation({
    mutationFn: (memberId: string) => appointPanelist(event.id, memberId),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (memberId: string) => removePanelist(event.id, memberId),
    onSuccess: refresh,
  });
  const close = useMutation({
    mutationFn: (anyway: boolean) => closeSeeding(event.id, anyway),
    onSuccess: refresh,
  });
  const reopen = useMutation({ mutationFn: () => reopenSeeding(event.id), onSuccess: refresh });

  const lists = data?.lists ?? [];
  const waiting =
    close.error instanceof Refusal && close.error.code === SEEDING_INCOMPLETE ? close.error : null;
  const error = appoint.error ?? remove.error ?? reopen.error ?? (waiting ? null : close.error);

  // No login, no panel: the database refuses a member without one, so they are not offered.
  const candidates = members.filter((m) => !m.managed);

  return (
    <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h2 className="micro">{event.name}</h2>
        <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-muted">
          {closed ? "Closed" : "Open"}
        </span>
      </div>

      {isPending ? (
        <p className="micro">Loading…</p>
      ) : (
        <>
          {lists.length === 0 ? (
            <p className="text-sm text-ink-muted">Nobody is on the panel yet.</p>
          ) : (
            <ul>
              {lists.map((list) => (
                <li
                  key={list.panelist.memberId}
                  className="border-b py-2"
                  style={{ borderColor: "var(--color-line-muted)" }}
                >
                  <div className="flex min-h-[44px] items-center gap-3">
                    <Avatar name={list.panelist.displayName} url={list.panelist.avatarUrl} />
                    <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">
                      {list.panelist.displayName}
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-muted">
                      {listStatus(list, registered)}
                    </span>
                    {!locked ? (
                      <Button
                        type="button"
                        tone="quiet"
                        disabled={remove.isPending}
                        aria-label={`Remove ${list.panelist.displayName} from the panel`}
                        onClick={() => remove.mutate(list.panelist.memberId)}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </div>
                  {list.players.length > 0 || list.skipped.length > 0 ? (
                    <details className="mt-1">
                      <summary className="font-mono cursor-pointer text-[11px] text-ink-muted">
                        Their list
                      </summary>
                      <ol className="mt-1 text-sm">
                        {list.players.map((p) => (
                          <li key={p.memberId} className="flex gap-3 py-0.5">
                            <span className="font-mono w-6 text-[12px] text-ink-muted">
                              {p.place}
                            </span>
                            {p.displayName}
                          </li>
                        ))}
                      </ol>
                      {list.skipped.length > 0 ? (
                        <p className="mt-1 text-sm text-ink-muted">
                          <span className="font-mono text-[11px] uppercase tracking-[0.06em]">
                            Skipped
                          </span>{" "}
                          {list.skipped.map((p) => p.displayName).join(", ")}
                        </p>
                      ) : null}
                    </details>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {!locked ? (
            <div className="mt-4">
              <MemberPicker
                label="Add a panelist"
                members={candidates}
                chosen={lists.map((l) => l.panelist.memberId)}
                onPick={(memberId) => appoint.mutate(memberId)}
              />
            </div>
          ) : null}

          {(closed ? published : (data?.average ?? [])).length > 0 ? (
            <div className="mt-6">
              <h3 className="micro">{closed ? "Fixed average" : "Average so far"}</h3>
              <SeedingAverage entries={closed ? published : (data?.average ?? [])} />
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap gap-3">
            {started ? (
              <Notice>The auction has started, so seeding is fixed.</Notice>
            ) : closed ? (
              <Button type="button" disabled={reopen.isPending} onClick={() => reopen.mutate()}>
                Reopen seeding
              </Button>
            ) : (
              <Button
                type="button"
                tone="primary"
                disabled={close.isPending}
                onClick={() => close.mutate(false)}
              >
                Close seeding
              </Button>
            )}
          </div>

          {waiting ? (
            <div className="mt-2">
              <Notice tone="attention">{waiting.message}.</Notice>
              <Button type="button" disabled={close.isPending} onClick={() => close.mutate(true)}>
                Close anyway
              </Button>
              <p className="mt-2 text-sm text-ink-muted">
                The average will count only the lists that are up to date, and the audit trail will
                say who was missing.
              </p>
            </div>
          ) : null}
          {error ? <Notice tone="attention">{error.message}</Notice> : null}
        </>
      )}
    </section>
  );
}
